import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { Button, Fab, Grid, Modal, Paper, TableContainer, Tooltip } from '@mui/material';
import { useSnackbar } from 'notistack';
import React from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useParams } from 'react-router';
import * as assetApi from '../../api/app/assetApi';
import * as shiftApi from '../../api/app/shiftsApi';
import { AlertDialog } from '../../components/core/AlertDialog';
import { ApiError } from '../../components/core/ApiError/ApiError';
import DataGridTable from '../../components/core/DataGridTable/DataGridTable';
import { Loader } from '../../components/core/Loader/Loader';
import { PageHeader } from '../../components/core/PageHeader/PageHeader';
import { useTranslation } from '../../contexts/translation';
import { useDeleteDialog } from '../../hooks/useDeleteDialog';
import { AddAssetToShiftRequest, RemoveAssetFromShiftRequest } from '../../types/app/asset.types';
import { Shift } from '../../types/app/shifts.types';
import { ShiftToAssetAdd } from './ShiftToAssetAdd';
interface Props {
  isReadOnly: boolean;
}
export function ShiftToAssetList({ isReadOnly }: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { id } = useParams();
  const {
    isOpen: isDeleteDialogOpen,
    idToDelete,
    setDeleteAttempt,
    cancelDeleteAttempt,
    confirmDeleteAttempt,
  } = useDeleteDialog();
  const handleOnDelete = (id: number) => setDeleteAttempt(id);
  const [open, setOpen] = React.useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const { data, isLoading, isError } = useQuery<Shift[]>(['shift', id], () =>
    assetApi.getShiftsForAsset(Number(id)),
  );

  const {
    data: allShiftsData,
    isLoading: allShiftsIsLoading,
    isError: allShiftsIsError,
  } = useQuery<Shift[]>('shifts', shiftApi.getShiftList);

  const { mutate: mutateDeleteShift } = useMutation(assetApi.removeAssetFromShift);
  const { mutate: mutateAddShift } = useMutation(assetApi.addAssetToShift);

  const columns = [
    { headerName: t('page.shift.table-col-1'), field: 'name', width: 200 },
    {
      headerName: t('page.shift.table-col-2'),
      field: 'start',
      width: 200,
    },
    {
      headerName: t('page.shift.table-col-3'),
      field: 'end',
      width: 200,
    },
    {
      field: '',
      sortable: false,
      disableColumnMenu: true,
      hide: isReadOnly,
      minWidth: 150,
      flex: 1,
      align: 'right',
      renderCell: (params: any) => {
        return (
          <>
            {!isReadOnly && (
              <>
                <Tooltip title={t('general.action-buttons.delete')}>
                  <Button onClick={() => handleOnDelete(params.id)}>
                    <DeleteIcon />
                  </Button>
                </Tooltip>
              </>
            )}
          </>
        );
      },
    },
  ];
  const headerButtons = [
    <Fab key={0} color="primary" aria-label="add" size="medium" onClick={handleOpen}>
      <AddIcon />
    </Fab>,
  ];

  const handleOnAdd = (shiftId: number) => {
    let requestData = { id: Number(id), shiftId: shiftId } as AddAssetToShiftRequest;

    mutateAddShift(
      { ...requestData },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(['shift']);
          enqueueSnackbar(t('page.shift.item-added'), { variant: 'success' });
        },
        onError: (result: any) => {
          enqueueSnackbar(`${result}`, { variant: 'error' });
        },
      },
    );
  };

  const handleConfirm = (data: RemoveAssetFromShiftRequest) => {
    if (!idToDelete) return;

    data.id = Number(id);
    data.shiftId = idToDelete;

    mutateDeleteShift(data, {
      onSuccess: () => {
        enqueueSnackbar(t('page.shift.item-deleted'), { variant: 'success' });
        queryClient.invalidateQueries(['shift']);
        confirmDeleteAttempt();
      },
      onError: (result: any) => {
        enqueueSnackbar(`${result}`, { variant: 'error' });
      },
    });
  };

  if (isError || allShiftsIsError) return <ApiError />;
  if (isLoading || allShiftsIsLoading || !data || !allShiftsData) return <Loader />;
  return (
    <>
      <Grid item xs={12}>
        <PageHeader
          title={t('page.shift.main-headline')}
          buttons={!isReadOnly ? headerButtons : null}
          headlineVariant={'h5'}
        />
        <TableContainer component={Paper}>
          <div style={{ height: '30vh', width: '100%' }}>
            <DataGridTable height={'30vh'} data={data} columns={columns} autoPageSize={true} />
          </div>
          {!isReadOnly && (
            <Tooltip title={t('general.action-buttons.add')}>
              <Button onClick={handleOpen}>
                <AddIcon />
              </Button>
            </Tooltip>
          )}
        </TableContainer>
      </Grid>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <div>
          <ShiftToAssetAdd data={allShiftsData} shifts={data} onAddShift={handleOnAdd} />
        </div>
      </Modal>
      <AlertDialog
        open={isDeleteDialogOpen}
        title={t('general.action-buttons.delete')}
        content={t('general.messages.warning.delete-message')}
        handleClose={cancelDeleteAttempt}
        handleConfirm={handleConfirm}
      />
    </>
  );
}
